import { Component, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, map } from 'rxjs';

import { AppSidebar } from '@components/partials/app-sidebar/app-sidebar';
import { UserMenu } from '@components/partials/user-menu/user-menu';

const SIDEBAR_COLLAPSED_KEY = 'savant.portal.sidebar-collapsed';

@Component({
  selector: 'app-private',
  imports: [RouterOutlet, AppSidebar, UserMenu],
  templateUrl: './private.layout.html',
  styleUrl: './private.layout.css',
  host: {
    class: 'app',
    '[class.is-collapsed]': 'isCollapsed()',
    '[class.is-drawer-open]': 'isDrawerOpen()',
  },
})
export class PrivateLayout {
  private readonly router = inject(Router);

  protected readonly isCollapsed = signal(readCollapsedPreference());

  protected readonly isDrawerOpen = signal(false);

  protected readonly pageTitle = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this.deepestTitle()),
    ),
    { initialValue: this.deepestTitle() },
  );

  protected toggleCollapsed(): void {
    this.isCollapsed.update((collapsed) => {
      const next = !collapsed;
      writeCollapsedPreference(next);
      return next;
    });
  }

  protected toggleDrawer(): void {
    this.isDrawerOpen.update((open) => !open);
  }

  protected closeDrawer(): void {
    this.isDrawerOpen.set(false);
  }

  private deepestTitle(): string {
    let route = this.router.routerState.snapshot.root;
    while (route.firstChild) route = route.firstChild;
    return route.title ?? '';
  }
}

function readCollapsedPreference(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeCollapsedPreference(collapsed: boolean): void {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  } catch {}
}
